import { HttpStatusCode } from 'axios'
import { GetServerSideProps } from 'next'
import Layout from 'src/layouts/padrao'
import { USER_LOCAL_KEY } from 'src/services/auth'
import {
  getProductContent,
  getProductTechnicalSheet,
} from 'src/services/marketplace-api'
import {
  ProductResponse,
  TechnicalSheetResponse,
  Variation,
} from 'src/services/marketplace-api/dtos'
import { ProductTemplate } from 'src/templates/Product'
import { ProductDetailsUrlData } from 'src/types/ProductDetailsUrlData'
import { parseServerSideRawCookies } from 'src/util/cookie-domain'
import {
  findVariationByColorAndVoltage,
  isProductDetailsUrlCorrect,
  makeProductUrlFromData,
} from 'src/util/productVariation'

type ProductPageProps = {
  productContent: ProductResponse
  productVariation: Variation
  productTechnicalSheet: TechnicalSheetResponse[]
}

export const getServerSideProps: GetServerSideProps = async ({
  params,
  req,
}) => {
  const cookiesKeyIndex = req.rawHeaders.findIndex((header) =>
    header.match(/Cookie/i)
  )
  const cookiesValueIndex = cookiesKeyIndex + 1
  const cookies = parseServerSideRawCookies(req.rawHeaders[cookiesValueIndex])
  const accessToken = cookies[USER_LOCAL_KEY]
    ? JSON.parse(cookies[USER_LOCAL_KEY]).access_token
    : null

  const currentUrlData: ProductDetailsUrlData = {
    description: params?.produto?.[0],
    id: params?.produto?.[1] ? Number(params?.produto?.[1]) : undefined,
    colorSlug: params?.produto?.[2],
    voltageSlug: params?.produto?.[3],
  }

  if (!currentUrlData.id) {
    return {
      notFound: true,
    }
  }

  const [product, technicalSheet] = await Promise.all([
    getProductContent(currentUrlData.id, accessToken),
    getProductTechnicalSheet(currentUrlData.id),
  ])

  if (product.status === HttpStatusCode.NotFound) {
    return {
      notFound: true,
    }
  }

  if (!product.data?.variacoes.length || !technicalSheet.data) {
    return {
      redirect: {
        destination: '/',
        permanent: false,
      },
    }
  }

  const variationByColorAndVoltage = findVariationByColorAndVoltage(
    product.data?.variacoes,
    currentUrlData.colorSlug,
    currentUrlData.voltageSlug
  )

  const productVariation =
    variationByColorAndVoltage ?? product.data?.variacoes[0]

  const expectedUrlData: ProductDetailsUrlData = {
    description: product.data?.slug,
    id: currentUrlData.id,
    colorSlug: productVariation.combinacoes[0].valor_slug,
    voltageSlug: productVariation.combinacoes[1].valor_slug,
    queryParams: req.url?.split('?')[1],
  }

  if (!isProductDetailsUrlCorrect(currentUrlData, expectedUrlData)) {
    const productUrl = makeProductUrlFromData(expectedUrlData)

    return {
      redirect: {
        destination: productUrl,
        permanent: true,
      },
    }
  }

  return {
    props: {
      productContent: product.data,
      productTechnicalSheet: technicalSheet.data,
      productVariation,
    },
  }
}

export default function ProductPage({
  productContent,
  productVariation,
  productTechnicalSheet,
}: ProductPageProps) {
  return (
    <Layout pageTopButton={true}>
      <ProductTemplate
        productContent={productContent}
        productTechnicalSheet={productTechnicalSheet}
        productVariation={productVariation}
      />
    </Layout>
  )
}
